import { Divider, Flex } from 'antd';
import { FC, useContext, useRef } from 'react';
import { type NodeProps, Position } from '@xyflow/react';
import styled from 'styled-components';
import { decodeClassifierMappingOptionValue } from '../../../../../../classifier-mapping';
import { Bleed, TextEllipsis } from '../../../../../../common/components';
import { FormField, FormSelect } from '../../../../../../common/form';
import { FormSelectSelectionLabel } from '../../../../../../common/form/input/FormSelectSelectionLabel';
import { formatCodeName } from '../../../../../../common/utils';
import { AlgorithmGraphReadOnlyField } from '../../../../AlgorithmGraphReadOnlyField';
import { GraphCard } from '../../../../GraphCard';
import { GraphCardTitle } from '../../../../GraphCardTitle';
import { AlgorithmMappingClassifierMappingFormSelect } from '../../../../mappings/AlgorithmMappingClassifierMappingFormSelect';
import { DbSourceDimensionFormField } from '../../../../mappings/DbSourceDimensionFormField';
import {
  createAlgorithmSourceDimensionsOptionGroup,
  decodeAlgorithmSourceDimensionOptionValue,
} from '../../../../sources/utils';
import { getAlgorithmSourceLabel } from '../../../../utils';
import type {
  AlgorithmJoinsAndMappingsGraphNodeOfType,
  AlgorithmJoinsAndMappingsGraphValidFormDataType,
} from '../../../schema/types';
import { AlgorithmJoinsWithMappingsGraphContext } from '../../AlgorithmJoinsWithMappingsGraphContext';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useNodeIndex } from '../../useNodeIndex';
import { AddRemoveAlgorithmNodeButtons } from '../AddRemoveAlgorithmNodeButtons';
import { AlgorithmJoinsAndMappingsGraphHandle } from '../AlgorithmJoinsAndMappingsGraphHandle';
import translations from './AlgorithmMappingNode.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-mapping-node',
);
addTranslations(translations);

export type AlgorithmMappingNodeProps = NodeProps<
  AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmMapping'>
>;

export const AlgorithmMappingNode: FC<AlgorithmMappingNodeProps> = (props) => {
  const { id, data, selected } = props;
  const { alias, classifierMapping, sourceDimension } = data.algorithmMapping;
  const index = useNodeIndex(id);
  const baseName = `nodes.${index}.data.algorithmMapping` as const;
  const { t } = useTranslation();
  const {
    readOnly,
    algorithmSourcesById,
    mappingJoinTypeOptions,
    isDbSources,
  } = useContext(AlgorithmJoinsWithMappingsGraphContext);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const { algorithmSourceId, dimensionCode } = sourceDimension
    ? decodeAlgorithmSourceDimensionOptionValue(sourceDimension)
    : {};

  const { classifierMappingCode, classifierMappingName } = classifierMapping
    ? decodeClassifierMappingOptionValue(classifierMapping)
    : {};

  return (
    <GraphCard
      ref={cardRef}
      size="small"
      title={
        <GraphCardTitle tagLabel={t('mapping')} tagColor="red">
          {!readOnly && (
            <AddRemoveAlgorithmNodeButtons nodeId={id} nodeIndex={index} />
          )}
        </GraphCardTitle>
      }
      className={selected ? 'selected' : undefined}
    >
      <Flex vertical gap="middle">
        {readOnly ? (
          <AlgorithmGraphReadOnlyField label={t('joinType')}>
            <TextEllipsis>
              {
                mappingJoinTypeOptions.find(
                  (o) => o.value === data.algorithmMapping.joinTypeCode,
                )?.label
              }
            </TextEllipsis>
          </AlgorithmGraphReadOnlyField>
        ) : (
          <FormField
            name={`${baseName}.joinTypeCode`}
            required
            label={t('joinType')}
          >
            <FormSelect
              name={`${baseName}.joinTypeCode`}
              className="nodrag"
              options={mappingJoinTypeOptions}
            />
          </FormField>
        )}
        {readOnly ? (
          <AlgorithmGraphReadOnlyField label={t('source')}>
            {isDbSources
              ? algorithmSourcesById.has(algorithmSourceId!) && (
                  <TextEllipsis>
                    {getAlgorithmSourceLabel(
                      algorithmSourcesById.get(algorithmSourceId!)!,
                    )}
                  </TextEllipsis>
                )
              : algorithmSourcesById.has(algorithmSourceId!) && (
                  <FormSelectSelectionLabel
                    ellipsis
                    supertitle={getAlgorithmSourceLabel(
                      algorithmSourcesById.get(algorithmSourceId!)!,
                    )}
                  >
                    {formatCodeName(
                      algorithmSourcesById
                        .get(algorithmSourceId!)!
                        .dimensions.find((d) => d.code === dimensionCode)!,
                    )}
                  </FormSelectSelectionLabel>
                )}
          </AlgorithmGraphReadOnlyField>
        ) : isDbSources ? (
          <DbSourceDimensionFormField<AlgorithmJoinsAndMappingsGraphValidFormDataType>
            name={`${baseName}.sourceDimension` as const}
            label={t('algorithmSource')}
            algorithmSourcesById={algorithmSourcesById}
          />
        ) : (
          <FormField
            name={`${baseName}.sourceDimension`}
            required
            label={t('sourceDimension')}
          >
            <FormSelect
              name={`${baseName}.sourceDimension`}
              className="nodrag"
              optionLabelProp="displayLabel"
              options={Array.from(algorithmSourcesById.values()).map(
                (algorithmSource) =>
                  createAlgorithmSourceDimensionsOptionGroup(algorithmSource),
              )}
              allowClear
            />
          </FormField>
        )}
        <Bleed direction="horizontal" size="sm">
          <Divider style={{ margin: 0 }} />
        </Bleed>
        {readOnly ? (
          <AlgorithmGraphReadOnlyField
            label={t('classifierMapping')}
            style={{ position: 'relative' }}
          >
            <AlgorithmJoinsAndMappingsGraphHandle
              nodeId={id}
              type="target"
              multipleConnections
              position={Position.Left}
            />
            <TextEllipsis>
              {`${classifierMappingCode} - ${classifierMappingName}`}
            </TextEllipsis>
            <AlgorithmJoinsAndMappingsGraphHandle
              nodeId={id}
              type="source"
              multipleConnections
              position={Position.Right}
            />
          </AlgorithmGraphReadOnlyField>
        ) : (
          <FormField
            name={`${baseName}.classifierMapping`}
            required
            label={t('classifierMapping')}
          >
            <ItemFieldWrapper>
              <AlgorithmJoinsAndMappingsGraphHandle
                nodeId={id}
                type="target"
                multipleConnections
                position={Position.Left}
              />
              <AlgorithmMappingClassifierMappingFormSelect
                name={`${baseName}.classifierMapping`}
                areDbSources={isDbSources}
                className="nodrag"
              />
              <AlgorithmJoinsAndMappingsGraphHandle
                nodeId={id}
                type="source"
                multipleConnections
                position={Position.Right}
              />
            </ItemFieldWrapper>
          </FormField>
        )}
        {readOnly ? (
          <AlgorithmGraphReadOnlyField label={t('alias')}>
            <TextEllipsis>{alias}</TextEllipsis>
          </AlgorithmGraphReadOnlyField>
        ) : (
          <FormField name={`${baseName}.alias`} required label={t('alias')} />
        )}
      </Flex>
    </GraphCard>
  );
};

const ItemFieldWrapper = styled(Flex)`
  position: relative;
  width: 100%;
`;
